import { Popover } from "@circleco/compass/components/Popover";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import { useCallback, useState } from "react";
import type { ElementType } from "react";

import { classNames } from "@/react/helpers/twMergeWithCN";

import { useEmojiPickerV4Context } from "./EmojiPickerV4Context";
import { CustomPicker } from "./Pickers/CustomPicker";
import { EmojiPicker } from "./Pickers/EmojiPicker";
import { IconPicker } from "./Pickers/IconPicker";
import { ShapePicker } from "./Pickers/ShapePicker";
import { EmojiPickerV4Tabs } from "./Tabs";

export const EmojiPickerV4Popover = () => {
  const {
    as,
    buttonAriaLabel,
    target,
    targetWrapperClassName,
    activeTab,
    isCustomFileUploadProcessing,
    onOpenChange,
  } = useEmojiPickerV4Context();

  const TriggerTag = (as ?? "span") as ElementType;
  const triggerTypeProps =
    TriggerTag === "button" ? { type: "button" as const } : {};

  const [isOpen, setIsOpen] = useState(false);
  const isPopoverOpen = isOpen || isCustomFileUploadProcessing;

  const handleOpenChange = useCallback(
    (isNextOpen: boolean) => {
      setIsOpen(isNextOpen);
      onOpenChange?.(isNextOpen);
    },
    [onOpenChange],
  );

  const dismiss = useCallback(
    () => handleOpenChange(false),
    [handleOpenChange],
  );

  return (
    <Popover
      trigger={
        <TriggerTag
          {...triggerTypeProps}
          className={classNames("inline-flex", targetWrapperClassName)}
          aria-label={buttonAriaLabel}
          data-testid="emoji-picker-button"
        >
          {buttonAriaLabel ? (
            <Tooltip side="bottom" content={buttonAriaLabel}>
              {target}
            </Tooltip>
          ) : (
            target
          )}
        </TriggerTag>
      }
      nativeButton={TriggerTag === "button"}
      open={isPopoverOpen}
      onOpenChange={handleOpenChange}
      side="bottom"
      align="start"
      sideOffset={16}
    >
      <div
        className="w-[28rem] max-w-[calc(100vw-3rem)]"
        data-testid="emoji-picker"
      >
        <EmojiPickerV4Tabs dismiss={dismiss} />
        {activeTab.id === "emoji" && <EmojiPicker dismiss={dismiss} />}
        {activeTab.id === "shapes" && <ShapePicker dismiss={dismiss} />}
        {activeTab.id === "icons" && <IconPicker dismiss={dismiss} />}
        {activeTab.id === "custom" && <CustomPicker dismiss={dismiss} />}
      </div>
    </Popover>
  );
};
